import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { X, Loader2, LogIn, UserPlus, KeyRound, ShieldCheck, AlertCircle } from 'lucide-react';
import { useAuth, formatApiErrorDetail } from '../context/AuthContext';

const MODES = { LOGIN: 'login', REGISTER: 'register', FORGOT: 'forgot' };
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AuthModal = ({ isOpen, onClose, onOpenTerms }) => {
  const { login, register, forgotInit, forgotVerify, fetchSecurityQuestions } = useAuth();
  const [mode, setMode] = useState(MODES.LOGIN);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [flags, setFlags] = useState({ registration_enabled: false, password_reset_enabled: false });

  // shared
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  // register
  const [fullName, setFullName] = useState('');
  const [securityQuestion, setSecurityQuestion] = useState('');
  const [securityAnswer, setSecurityAnswer] = useState('');
  const [questions, setQuestions] = useState([]);
  const [tcAccepted, setTcAccepted] = useState(false);

  // forgot
  const [forgotStage, setForgotStage] = useState('email'); // 'email' | 'reset'
  const [forgotQuestion, setForgotQuestion] = useState('');
  const [forgotAnswer, setForgotAnswer] = useState('');
  const [newPassword, setNewPassword] = useState('');

  const scrollRef = useRef(null);
  // Whenever a success or error banner appears, scroll the modal body to the top
  // so the user always sees the latest status message (esp. pending registration).
  useEffect(() => {
    if ((success || error) && scrollRef.current) {
      scrollRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [success, error]);

  useEffect(() => {
    if (!isOpen) return;
    const esc = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen) return;
    axios.get(`${API}/auth/flags`)
      .then((res) => setFlags(res.data || flags))
      .catch(() => { /* leave defaults */ });
  }, [isOpen, flags]);

  useEffect(() => {
    // If user lands on a disabled mode, push them back to login.
    if (mode === MODES.REGISTER && !flags.registration_enabled) setMode(MODES.LOGIN);
    if (mode === MODES.FORGOT && !flags.password_reset_enabled) setMode(MODES.LOGIN);
  }, [mode, flags.registration_enabled, flags.password_reset_enabled]);

  useEffect(() => {
    if (mode === MODES.REGISTER && questions.length === 0) {
      fetchSecurityQuestions().then(setQuestions).catch(() => setQuestions([]));
    }
  }, [mode, questions.length, fetchSecurityQuestions]);

  const resetState = () => {
    setError(''); setSuccess(''); setEmail(''); setPassword('');
    setFullName(''); setSecurityQuestion(''); setSecurityAnswer('');
    setForgotStage('email'); setForgotQuestion(''); setForgotAnswer(''); setNewPassword('');
    setTcAccepted(false);
  };

  const switchMode = (next) => { resetState(); setMode(next); };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      const u = await login(email, password);
      setSuccess(`Welcome back, ${u.full_name || u.email}.`);
      setTimeout(() => { resetState(); onClose(); }, 700);
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');
    if (!securityQuestion) { setError('Please select a security question.'); return; }
    if (password.length < 8) { setError('Password must be at least 8 characters.'); return; }
    if (!tcAccepted) { setError('You must accept the Terms & Conditions to register.'); return; }
    setLoading(true);
    try {
      const u = await register({
        email,
        password,
        full_name: fullName,
        security_question: securityQuestion,
        security_answer: securityAnswer,
        tc_accepted: true,
      });
      if (u && u.status === 'pending') {
        setSuccess(
          u.message ||
            "Registration submitted. Your account is pending administrator approval — you'll receive a no-reply email once it's activated."
        );
        // Keep modal open so the user sees the message; clear form fields only.
        setEmail(''); setPassword(''); setFullName('');
        setSecurityQuestion(''); setSecurityAnswer('');
      } else {
        setSuccess(`Welcome aboard, ${u.full_name || u.email}.`);
        setTimeout(() => { resetState(); onClose(); }, 800);
      }
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotInit = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      const q = await forgotInit(email);
      setForgotQuestion(q);
      setForgotStage('reset');
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotVerify = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      await forgotVerify({ email, security_answer: forgotAnswer, new_password: newPassword });
      setSuccess('Password reset successful. You are now signed in.');
      setTimeout(() => { resetState(); onClose(); }, 900);
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "w-full px-4 py-2.5 bg-slate-950 border border-slate-850 font-mono text-xs rounded text-white focus:outline-none focus:border-[#00f0ff] focus:ring-1 focus:ring-[#00f0ff]/50 transition-all placeholder:text-slate-600";
  const labelCls = "font-mono text-[10px] uppercase tracking-widest font-semibold text-slate-400 block mb-1.5";
  const submitCls = "w-full py-3 bg-gradient-to-r from-[#00f0ff] to-[#0070ff] hover:from-[#0070ff] hover:to-[#00f0ff] disabled:opacity-40 text-slate-950 font-display font-black text-xs tracking-widest uppercase rounded cursor-pointer transition-all flex items-center justify-center gap-2";

  return (
    <AnimatePresence>
      {isOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto bg-black/85 backdrop-blur-md"
          onClick={onClose}
          data-testid="auth-modal"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md glass-card rounded-xl border border-slate-800 overflow-hidden shadow-2xl relative flex flex-col my-8"
            style={{ maxHeight: 'calc(100vh - 4rem)' }}
          >
            <div className="bg-slate-950/80 p-4 border-b border-slate-800 flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <span className="w-2 h-2 rounded-full bg-[#00f0ff] signal-dot" />
                <h2 className="font-display font-semibold text-sm tracking-widest text-[#00f0ff] uppercase">
                  {mode === MODES.LOGIN && 'Sign In · Console'}
                  {mode === MODES.REGISTER && 'New Operator · Console'}
                  {mode === MODES.FORGOT && 'Recovery Channel'}
                </h2>
              </div>
              <button onClick={onClose} className="p-1.5 rounded-full hover:bg-white/10 text-slate-400 hover:text-white transition-colors" data-testid="btn-close-auth">
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="flex border-b border-slate-900 bg-slate-950/40 text-[10px] font-mono tracking-widest uppercase">
              <button
                onClick={() => switchMode(MODES.LOGIN)}
                className={`flex-1 py-3 flex items-center justify-center gap-1.5 transition-all cursor-pointer ${mode === MODES.LOGIN ? 'bg-[#00f0ff]/5 text-[#00f0ff] border-b-2 border-[#00f0ff]' : 'text-slate-500 hover:text-slate-300'}`}
                data-testid="auth-tab-login"
              >
                <LogIn className="w-3 h-3" /> Login
              </button>
              {flags.registration_enabled && (
                <button
                  onClick={() => switchMode(MODES.REGISTER)}
                  className={`flex-1 py-3 flex items-center justify-center gap-1.5 transition-all cursor-pointer ${mode === MODES.REGISTER ? 'bg-[#39ff14]/5 text-[#39ff14] border-b-2 border-[#39ff14]' : 'text-slate-500 hover:text-slate-300'}`}
                  data-testid="auth-tab-register"
                >
                  <UserPlus className="w-3 h-3" /> Register
                </button>
              )}
              {flags.password_reset_enabled && (
                <button
                  onClick={() => switchMode(MODES.FORGOT)}
                  className={`flex-1 py-3 flex items-center justify-center gap-1.5 transition-all cursor-pointer ${mode === MODES.FORGOT ? 'bg-[#9d4edd]/5 text-[#9d4edd] border-b-2 border-[#9d4edd]' : 'text-slate-500 hover:text-slate-300'}`}
                  data-testid="auth-tab-forgot"
                >
                  <KeyRound className="w-3 h-3" /> Forgot
                </button>
              )}
            </div>

            <div ref={scrollRef} className="p-6 overflow-y-auto">
              {error && (
                <div className="mb-4 p-3 bg-rose-500/10 border border-rose-500/30 rounded text-rose-300 text-xs flex items-start gap-2" data-testid="auth-error">
                  <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                  <span className="leading-relaxed">{error}</span>
                </div>
              )}
              {success && (
                <div className="mb-4 p-3 bg-emerald-500/10 border border-emerald-500/30 rounded text-[#39ff14] text-xs flex items-start gap-2" data-testid="auth-success">
                  <ShieldCheck className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                  <span className="leading-relaxed">{success}</span>
                </div>
              )}

              {mode === MODES.LOGIN && (
                <form onSubmit={handleLogin} className="space-y-4" data-testid="form-login">
                  <div>
                    <label className={labelCls}>Email</label>
                    <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} placeholder="you@domain.com" data-testid="input-login-email" />
                  </div>
                  <div>
                    <label className={labelCls}>Password</label>
                    <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} placeholder="••••••••" data-testid="input-login-password" />
                  </div>
                  <button type="submit" disabled={loading} className={submitCls} data-testid="btn-submit-login">
                    {loading ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Authenticating…</> : <><LogIn className="w-3.5 h-3.5" /> Sign In</>}
                  </button>
                  {flags.registration_enabled && (
                    <p className="font-mono text-[10px] text-slate-500 text-center pt-2">
                      No account? <button type="button" onClick={() => switchMode(MODES.REGISTER)} className="text-[#39ff14] hover:underline">Register here</button>
                    </p>
                  )}
                  {!flags.registration_enabled && !flags.password_reset_enabled && (
                    <p className="font-mono text-[10px] text-slate-500 text-center pt-2">
                      New registration and password reset are currently disabled.
                    </p>
                  )}
                </form>
              )}

              {mode === MODES.REGISTER && (
                <form onSubmit={handleRegister} className="space-y-4" data-testid="form-register">
                  <div>
                    <label className={labelCls}>Full Name</label>
                    <input type="text" required maxLength={64} value={fullName} onChange={(e) => setFullName(e.target.value)} className={inputCls} placeholder="Ada Lovelace" data-testid="input-register-name" />
                  </div>
                  <div>
                    <label className={labelCls}>Email</label>
                    <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} placeholder="you@domain.com" data-testid="input-register-email" />
                  </div>
                  <div>
                    <label className={labelCls}>Password (min 8)</label>
                    <input type="password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} placeholder="••••••••" data-testid="input-register-password" />
                  </div>
                  <div>
                    <label className={labelCls}>Security Question</label>
                    <select required value={securityQuestion} onChange={(e) => setSecurityQuestion(e.target.value)} className={inputCls + ' cursor-pointer'} data-testid="input-register-question">
                      <option value="">— Choose a recovery challenge —</option>
                      {questions.map((q) => <option key={q} value={q}>{q}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className={labelCls}>Security Answer</label>
                    <input type="text" required maxLength={128} value={securityAnswer} onChange={(e) => setSecurityAnswer(e.target.value)} className={inputCls} placeholder="Answer (case-insensitive)" data-testid="input-register-answer" />
                  </div>
                  <label className="flex items-start gap-2.5 p-3 border border-slate-800 rounded bg-slate-950/60 cursor-pointer hover:border-[#9d4edd]/50 transition-colors" data-testid="tc-checkbox-wrap">
                    <input
                      type="checkbox"
                      checked={tcAccepted}
                      onChange={(e) => setTcAccepted(e.target.checked)}
                      className="mt-0.5 w-4 h-4 accent-[#9d4edd] cursor-pointer shrink-0"
                      data-testid="checkbox-tc"
                    />
                    <span className="text-[11px] text-slate-300 leading-relaxed font-light">
                      I have read and accept the{' '}
                      <button
                        type="button"
                        onClick={() => onOpenTerms && onOpenTerms()}
                        className="text-[#9d4edd] hover:text-[#c89cff] hover:underline font-semibold cursor-pointer"
                        data-testid="btn-open-terms-register"
                      >
                        Terms &amp; Conditions
                      </button>
                      . I understand the site admin may revoke my access or delete my account without notice, and that all content is informational only.
                    </span>
                  </label>
                  <button type="submit" disabled={loading || !tcAccepted} className={submitCls} data-testid="btn-submit-register">
                    {loading ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Provisioning…</> : <><UserPlus className="w-3.5 h-3.5" /> Create Account</>}
                  </button>
                </form>
              )}

              {mode === MODES.FORGOT && forgotStage === 'email' && (
                <form onSubmit={handleForgotInit} className="space-y-4" data-testid="form-forgot-email">
                  <p className="text-xs text-slate-400 font-light leading-relaxed">
                    Enter your registered email. We&apos;ll retrieve your recovery challenge question.
                  </p>
                  <div>
                    <label className={labelCls}>Email</label>
                    <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} placeholder="you@domain.com" data-testid="input-forgot-email" />
                  </div>
                  <button type="submit" disabled={loading} className={submitCls} data-testid="btn-forgot-init">
                    {loading ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Looking up…</> : <><KeyRound className="w-3.5 h-3.5" /> Continue</>}
                  </button>
                </form>
              )}

              {mode === MODES.FORGOT && forgotStage === 'reset' && (
                <form onSubmit={handleForgotVerify} className="space-y-4" data-testid="form-forgot-reset">
                  <div className="p-3 bg-[#9d4edd]/10 border border-[#9d4edd]/30 rounded">
                    <div className="font-mono text-[9px] text-[#9d4edd] uppercase tracking-widest mb-1">Your Challenge Question</div>
                    <div className="text-xs text-slate-200 font-medium" data-testid="forgot-question">{forgotQuestion}</div>
                  </div>
                  <div>
                    <label className={labelCls}>Your Answer</label>
                    <input type="text" required value={forgotAnswer} onChange={(e) => setForgotAnswer(e.target.value)} className={inputCls} placeholder="Answer (case-insensitive)" data-testid="input-forgot-answer" />
                  </div>
                  <div>
                    <label className={labelCls}>New Password (min 8)</label>
                    <input type="password" required minLength={8} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} className={inputCls} placeholder="••••••••" data-testid="input-forgot-new-password" />
                  </div>
                  <button type="submit" disabled={loading} className={submitCls} data-testid="btn-forgot-verify">
                    {loading ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Resetting…</> : <><ShieldCheck className="w-3.5 h-3.5" /> Reset Password</>}
                  </button>
                  <button type="button" onClick={() => setForgotStage('email')} className="w-full text-[10px] font-mono text-slate-500 hover:text-slate-300">
                    ← Use a different email
                  </button>
                </form>
              )}
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};
